import React, {useEffect, useMemo} from "react"
import {
	ApplicationStatus,
	Assignee,
	DecisionMethodType,
	exportApplications,
	findApplication,
} from "../../resources/application"
import {
	useAccessToken,
	useIsBankUser,
	useIsUnitComplianceUser,
	useIsUnitSuperUser,
	useUserType,
} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {usePaging} from "../../hooks/usePaging"
import {useQueryState} from "use-location-state"
import SearchBox from "../../components/SearchBox/SearchBox"
import {Filter} from "../Filter/Filter"
import {ErrorDocument} from "../../resources/common"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import classNames from "classnames"
import {startCase} from "lodash"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {useIsAllowedToShowAssigneeFilter} from "../../services/permission"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {Org} from "../../resources/org"
import {AllowedApplicationsColumns} from "./Application.types"
import ApplicationsTable from "./ApplicationsTable"

interface ApplicationsProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedApplicationsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	setNonDeniedIndividualApplicationsNum?: (n: number) => void
	setNonDeniedBusinessApplicationsNum?: (n: number) => void
	refreshToken: number
	showTabsOnHeader?: boolean
}

export function ArchivedBadge() {
	return (
		<div className={classNames("is-flex", "ml-2")}>
			<div className={classNames("status-badge", "archived")}>Archived</div>
		</div>
	)
}

export function Applications({
	limit = 25,
	includedColumns,
	fullHeight,
	enableTitle,
	setNonDeniedBusinessApplicationsNum,
	setNonDeniedIndividualApplicationsNum,
	refreshToken,
	showTabsOnHeader,
}: ApplicationsProps) {
	const prefix = "app"
	const userType = useUserType()
	const {addToast} = useToasts()
	const statusesOptions = new Map(
		[
			ApplicationStatus.Approved,
			ApplicationStatus.Denied,
			ApplicationStatus.PendingReview,
			ApplicationStatus.AwaitingDocuments,
			ApplicationStatus.AwaitingInformation,
			ApplicationStatus.Pending,
			ApplicationStatus.Canceled,
		].map((v) => [v, startCase(v)])
	)
	const [statuses, setStatuses] = useQueryState(`${prefix}-filter[status]`, Array.from(statusesOptions.keys()))
	const decisionMethodOptions = new Map(
		[DecisionMethodType.Manually, DecisionMethodType.InProgress, DecisionMethodType.Automatically].map((v) => [
			v,
			startCase(v),
		])
	)
	const [decisionMethods, setDecisionMethods] = useQueryState(
		`${prefix}-filter[decisionMethod]`,
		Array.from(decisionMethodOptions.keys())
	)

	const assigneeOptions = new Map(Object.values(Assignee).map((v) => [v, startCase(v)]))
	const [assignees, setAssignees] = useQueryState<Assignee[]>(`${prefix}-filter[assignee]`, [])

	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const accessToken = useAccessToken()
	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findApplication(accessToken, offset, limit, statuses, sortBy, query, filteredOrgs, decisionMethods, assignees),
		(x) => x.data.length,
		[sortBy, query, [...statuses, ...filteredOrgs, ...decisionMethods, ...assignees].join(","), refreshToken],
		prefix,
		true
	)
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Applications", 500, onSearch)
	const isFiltering =
		filteredOrgs.length !== 0 ||
		query !== "" ||
		statuses.length !== statusesOptions.size ||
		decisionMethods.length !== decisionMethodOptions.size ||
		assignees.length !== 0

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const assigneeFilter = (
		<Filter
			statuses={assignees}
			setStatuses={setAssignees}
			onFilterFunc={() => reset(limit)}
			options={assigneeOptions}
			title="Assignee"
		/>
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const decisionMethodFilter = (
		<Filter
			statuses={decisionMethods}
			setStatuses={setDecisionMethods}
			onFilterFunc={() => reset(limit)}
			options={decisionMethodOptions}
			title="Decision Method"
		/>
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	async function exportApplicationsFunc() {
		const response = await exportApplications(accessToken, statuses, sortBy, query, filteredOrgs)

		response.match(
			(d) => downloadFile("Applications.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)

		return true
	}

	return (
		<DataTableCard className={"applications-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={searchBox}
				exportFunc={exportApplicationsFunc}
				filters={[
					orgFilter,
					statusFilter,
					...(useIsUnitSuperUser() || useIsUnitComplianceUser() || useIsBankUser() ? [decisionMethodFilter] : []),
					...(useIsAllowedToShowAssigneeFilter() ? [assigneeFilter] : []),
				]}
				title={enableTitle ? "Applications" : null}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: applications}) => {
					const nonDeniedIndividualApplicationsNum = applications.data.filter(
						(a) => a.type === "individualApplication" && a.attributes.status !== ApplicationStatus.Denied
					).length
					const nonDeniedBusinessApplicationsNum = applications.data.filter(
						(a) => a.type === "businessApplication" && a.attributes.status !== ApplicationStatus.Denied
					).length
					useEffect(() => {
						if (setNonDeniedBusinessApplicationsNum && nonDeniedIndividualApplicationsNum) {
							setNonDeniedBusinessApplicationsNum(nonDeniedIndividualApplicationsNum)
						}
						if (setNonDeniedIndividualApplicationsNum && nonDeniedBusinessApplicationsNum) {
							setNonDeniedIndividualApplicationsNum(nonDeniedBusinessApplicationsNum)
						}
					}, [])

					return (
						<ApplicationsTable
							applications={applications.data}
							include={applications.included}
							isFiltering={isFiltering}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							includedColumns={includedColumns}
							fullHeight={fullHeight}
							showTabsOnHeader={showTabsOnHeader}
							meta={applications.meta}
							sortFunction={toggleSort}
							sortBy={sortBy}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
